/**
 * Emails with clients, copied from each staff member's Gmail: the connection (Messages › Client
 * email) and the list, on the Messages page and on a client's page.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowUpRight, Mail, Paperclip } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { ask } from "@/components/dialogs";
import { Empty, ListSearch, Loading, Pager, Section, useServerPaged } from "@/components/page";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import type { Me } from "@/lib/session";
import { toast } from "@/lib/use-toast";

export type ClientEmail = {
  id: string;
  clientId: string;
  clientName?: string;
  threadId: string;
  direction: "in" | "out";
  fromAddress: string;
  fromName: string;
  toAddresses: string;
  subject: string;
  body: string;
  attachments: string[];
  sentAt: string;
  mailbox: string;
};

type Status = { configured: boolean; isDemo: boolean; connections: Array<{ userId: string; email: string; status: string; isDemo: boolean; gmail: boolean; gmailSyncedAt: string | null; gmailError: string | null }> };

const RESULT: Record<string, { title: string; description?: string; bad?: boolean }> = {
  connected: { title: "Gmail connected", description: "Emails with clients will appear here within a few minutes." },
  cancelled: { title: "Gmail wasn't connected", description: "You cancelled at Google.", bad: true },
  scopes: { title: "Gmail wasn't connected", description: "Allow every permission Google asks for, including reading your email.", bad: true },
  expired: { title: "That sign-in expired", description: "Click Connect Gmail again.", bad: true },
  error: { title: "Gmail couldn't be connected", description: "Try again. If it keeps failing, ask an owner to check the Google setup.", bad: true },
};

/** Your own Gmail: connect it, see when it was last read, or stop copying. */
export function GmailConnection({ me }: { me: Me }) {
  const { data, refetch } = useQuery<Status>({ queryKey: ["/api/google/status"] });
  useEffect(() => {
    const result = new URLSearchParams(window.location.search).get("gmail");
    const r = result ? RESULT[result] : undefined;
    if (!r) return;
    toast({ title: r.title, description: r.description, variant: r.bad ? "destructive" : undefined });
    window.history.replaceState(null, "", "/messages?tab=email");
  }, []);
  const connect = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send<{ url: string }>("POST", "/api/google/gmail/connect"), onSuccess: (r) => window.location.assign(r.url) });
  const stop = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", "/api/google/gmail/disconnect"),
    onSuccess: () => {
      toast({ title: "Gmail disconnected", description: "Emails already copied stay on the clients' records." });
      void refetch();
    },
  });
  if (!data) return null;
  const mine = data.connections.find((c) => c.userId === me.user?.id);
  const on = !!mine?.gmail && mine.status === "connected";
  const err = connect.error ?? stop.error;
  return (
    <Section
      title="Your Gmail"
      description="Connect your Gmail and every email you exchange with a client (their email address, or a portal user's) is copied onto that client's record, every few minutes. ClientFlow only reads: it never sends, changes or deletes your email, and nothing else in your mailbox is kept. Connecting also connects your Google Calendar for bookings, if it isn't connected already."
    >
      <div className="space-y-3 text-sm">
        {data.isDemo ? (
          <p className="text-muted-foreground">The demo firm's emails are samples; no mailbox is connected or read. Connect Gmail in your own firm.</p>
        ) : on ? (
          <p>
            <span className="font-medium text-emerald-700 dark:text-emerald-400">Connected</span>
            {mine.email ? ` as ${mine.email}` : ""}. {mine.gmailSyncedAt ? `Last checked ${formatDateTime(mine.gmailSyncedAt)}.` : "The first check runs within a few minutes and copies the last 30 days (up to 2,000 emails)."}
          </p>
        ) : mine?.status === "needs_reconnect" ? (
          <p className="text-destructive">Google stopped accepting ClientFlow's access. Connect again.</p>
        ) : (
          <p className="text-muted-foreground">Not connected.</p>
        )}
        {on && mine.gmailError && <p className="text-destructive">The last check failed: {mine.gmailError}</p>}
        {err && <p className="text-destructive">{errorMessage(err)}</p>}
        {!data.isDemo &&
          (!data.configured ? (
            <p className="text-xs text-muted-foreground">Google isn't set up on this server yet (Google client ID and secret in Replit Secrets).</p>
          ) : me.role === "readonly" ? null : on ? (
            <Button size="sm" variant="ghost" className="text-destructive" disabled={stop.isPending} onClick={() => void ask({ title: "Stop copying your email?", description: "New emails with clients stop being copied; emails already copied stay on the clients' records. Your calendar stays connected. To take back ClientFlow's permission to read your Gmail entirely, also disconnect your calendar (Firm Settings › Client communication › Booking › Hours), then connect the calendar again.", confirmLabel: "Stop copying", destructive: true }).then((ok) => ok && stop.mutate())}>
              Stop copying email
            </Button>
          ) : (
            <Button size="sm" disabled={connect.isPending} onClick={() => connect.mutate()}>
              <Mail className="h-4 w-4" /> Connect Gmail
            </Button>
          ))}
      </div>
    </Section>
  );
}

function EmailRow({ e, showClient, canRemove, onRemoved }: { e: ClientEmail; showClient?: boolean; canRemove: boolean; onRemoved: () => void }) {
  const [open, setOpen] = useState(false);
  const remove = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("DELETE", `/api/client-emails/${e.id}`), onSuccess: onRemoved });
  const Icon = e.direction === "in" ? ArrowDownLeft : ArrowUpRight;
  return (
    <li className="px-4 py-3">
      <button type="button" className="flex w-full min-w-0 items-start gap-3 text-left" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${e.direction === "in" ? "text-primary" : "text-muted-foreground"}`} aria-label={e.direction === "in" ? "Received" : "Sent"} />
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-baseline justify-between gap-3">
            <span className="truncate text-sm font-medium">{e.subject || "(no subject)"}</span>
            <span className="shrink-0 text-xs text-muted-foreground">{formatDateTime(e.sentAt)}</span>
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {e.direction === "in" ? `From ${e.fromName || e.fromAddress}` : `To ${e.toAddresses}`}
            {showClient && e.clientName ? ` · ${e.clientName}` : ""}
            {e.mailbox ? ` · ${e.mailbox}'s mailbox` : ""}
          </span>
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-3 pl-7">
          <p className="text-xs text-muted-foreground">
            From {e.fromName ? `${e.fromName} <${e.fromAddress}>` : e.fromAddress} · To {e.toAddresses}
          </p>
          <p className="whitespace-pre-wrap break-words text-sm">{e.body || "(no text)"}</p>
          {e.attachments.length > 0 && (
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <Paperclip className="h-3.5 w-3.5" /> {e.attachments.join(", ")} <span>(attachments stay in Gmail)</span>
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            {showClient && (
              <Link href={`/clients/${e.clientId}?tab=messages`} className="text-primary hover:underline">
                Open {e.clientName}
              </Link>
            )}
            {canRemove && (
              <button type="button" className="text-destructive hover:underline disabled:opacity-50" disabled={remove.isPending} onClick={() => void ask({ title: "Remove this email from ClientFlow?", description: "Only ClientFlow's copy is removed; the email stays in Gmail.", confirmLabel: "Remove", destructive: true }).then((ok) => ok && remove.mutate())}>
                Remove from ClientFlow
              </button>
            )}
          </div>
          {remove.error && <p className="text-sm text-destructive">{errorMessage(remove.error)}</p>}
        </div>
      )}
    </li>
  );
}

/** Emails with clients: one client's (on their page), or the latest across clients (Messages › Client email). */
export function ClientEmails({ me, clientId }: { me: Me; clientId?: string }) {
  const [q, setQ] = useState("");
  const { offset, size, paged } = useServerPaged(`${clientId ?? ""}${q.trim()}`);
  const params = new URLSearchParams({ offset: String(offset), limit: String(size) });
  if (!clientId && q.trim()) params.set("q", q.trim());
  const url = clientId ? `/api/clients/${clientId}/emails?${params}` : `/api/client-emails?${params}`;
  const { data, isLoading, refetch } = useQuery<{ items: ClientEmail[]; total: number }>({ queryKey: [url], placeholderData: (prev) => prev });
  const canRemove = me.role === "owner" || me.role === "admin";
  return (
    <div className="space-y-3">
      {!clientId && <ListSearch value={q} onChange={setQ} placeholder="Search by client, subject or sender" className="sm:w-full" />}
      {isLoading ? (
        <Loading />
      ) : !data?.items.length ? (
        <Empty>{q.trim() ? "No emails match your search." : clientId ? "No emails with this client yet. Emails appear here when someone at the firm has connected their Gmail (Messages › Client email)." : "No emails with clients yet. Connect your Gmail above; emails with clients appear within a few minutes."}</Empty>
      ) : (
        <>
          <ul className="divide-y divide-border rounded-lg border border-card-border bg-card">
            {data.items.map((e) => (
              <EmailRow key={e.id} e={e} showClient={!clientId} canRemove={canRemove} onRemoved={() => void refetch()} />
            ))}
          </ul>
          <Pager paged={paged(data.total)} noun="emails" />
        </>
      )}
    </div>
  );
}
